import { AlertTriangle, CreditCard, Server, ShieldAlert, TrendingDown, Zap } from "lucide-react";
import type { ReactNode } from "react";

import type { OverviewKpis } from "@busright/data-contract";
import { RAG, TrendChart } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { ClickableCard } from "@/components/ClickableCard";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import {
  InsightCallout,
  type InsightSeverity,
  type InsightStat,
} from "@/components/InsightCallout";
import { KpiCard } from "@/components/KpiCard";
import { NotesList } from "@/components/NotesList";
import { PageHeader } from "@/components/PageHeader";
import { RiskDonut } from "@/components/RiskDonut";
import { SkeletonLines } from "@/components/Skeleton";
import { useOverview } from "@/hooks/queries";
import { formatInt, formatPct, formatUsd, formatUsdCompact } from "@/lib/format";
import { seriesDelta, weekdaysOnly } from "@/lib/trend";

// The headline takeaway is computed from the loaded KPIs — never hardcoded. The lead signal is the
// at-risk device load (WARN+CRIT); severity escalates to CRITICAL only when critical devices exist.
interface OverviewInsight {
  readonly headline: string;
  readonly severity: InsightSeverity;
  readonly stats: readonly InsightStat[];
  readonly ctaTo: string;
  readonly ctaLabel: string;
}

// Lead with the worst tier present: CRITICAL → WARN → all-clear. Returns the headline + RAG accent.
const leadSignal = (
  kpis: OverviewKpis,
): { readonly headline: string; readonly severity: InsightSeverity } => {
  const crit = kpis.riskCounts.critical;
  if (crit > 0) {
    return {
      headline: `${formatInt(crit)} devices are CRITICAL (${formatPct(kpis.criticalSharePct)} of scored) — triage these first.`,
      severity: "critical",
    };
  }
  const warn = kpis.riskCounts.warn;
  if (warn > 0) {
    return {
      headline: `No CRITICAL devices, but ${formatInt(warn)} are in WARN — watch before they slip.`,
      severity: "warn",
    };
  }
  return {
    headline: "Fleet is healthy — no devices in WARN or CRITICAL across the scored population.",
    severity: "ok",
  };
};

const buildInsight = (kpis: OverviewKpis): OverviewInsight => {
  const lead = leadSignal(kpis);
  const stats: InsightStat[] = [
    { label: "% critical", value: formatPct(kpis.criticalSharePct) },
    { label: "AR exposure", value: formatUsdCompact(kpis.arCurrentBalanceUsd) },
    { label: "unmatched recipients (DQ)", value: formatInt(kpis.districtsUnmatched) },
  ];
  return {
    headline: lead.headline,
    severity: lead.severity,
    stats,
    ctaTo: "/health",
    ctaLabel: "Open Fleet Health",
  };
};

// Executive Command Center — the landing surface. Hero KPI band, risk composition donut, a
// weekday-only health-trend line, $ exposure, and a thermal/anomaly readout. The persona lens
// reorders these cards (never the numbers).

interface CardSpec {
  readonly key: string;
  readonly node: ReactNode;
  readonly className?: string;
}

export const CommandCenterPage = (): ReactNode => {
  const { data, isLoading, isError, error } = useOverview();

  const meta = data?.meta;
  const kpis = data?.data.kpis;
  const trend = data ? weekdaysOnly(data.data.healthTrend) : [];
  const trendDelta = seriesDelta(trend);
  const insight = kpis ? buildInsight(kpis) : null;

  const insightBanner: ReactNode = insight ? (
    <InsightCallout
      headline={insight.headline}
      severity={insight.severity}
      stats={insight.stats}
      cta={{ label: insight.ctaLabel, to: insight.ctaTo }}
    />
  ) : null;

  const kpiBand: ReactNode = (
    <div className="grid kpi-band">
      <KpiCard
        label="Fleet (distinct serials)"
        icon={Server}
        loading={isLoading}
        value={kpis ? formatInt(kpis.fleetDistinctSerials.value) : "—"}
        delta={kpis?.fleetDistinctSerials}
        source="esper_fleet_roster"
      />
      <KpiCard
        label="Devices scored"
        icon={ShieldAlert}
        loading={isLoading}
        value={kpis ? formatInt(kpis.devicesScored.value) : "—"}
        delta={kpis?.devicesScored}
        source="rpt_hw_device_latest"
      />
      <ClickableCard to="/health" label="Open Fleet Health — drill into critical devices">
        <KpiCard
          label="% critical"
          icon={AlertTriangle}
          loading={isLoading}
          value={kpis ? formatPct(kpis.criticalSharePct) : "—"}
          valueColor={kpis && kpis.criticalSharePct > 0 ? RAG.CRITICAL : undefined}
          source="rpt_hw_device_latest.risk_tier"
        />
      </ClickableCard>
      <ClickableCard to="/finance" label="Open Finance — AR and credit posture">
        <KpiCard
          label="AR $ exposure"
          icon={CreditCard}
          loading={isLoading}
          value={kpis ? formatUsd(kpis.arCurrentBalanceUsd) : "—"}
          source="stg_e2_credits.ar_current_balance"
        />
      </ClickableCard>
    </div>
  );

  const cards: CardSpec[] = [
    {
      key: "risk",
      node: (
        <ClickableCard to="/districts" label="Open Districts — at-risk worklist by district">
          <ChartCard
            title="Risk composition"
            subtitle="Scored devices by risk tier (OK / WARN / CRIT) — open the at-risk worklist"
            source="rpt_hw_device_latest"
            loading={isLoading}
          >
            {kpis ? <RiskDonut counts={kpis.riskCounts} /> : <EmptyState title="No risk data" />}
          </ChartCard>
        </ClickableCard>
      ),
    },
    {
      key: "trend",
      className: "span-2",
      node: (
        <ChartCard
          title="Fleet health trend"
          subtitle="Composite health score · weekday-only"
          source="rpt_hw_device_latest"
          loading={isLoading}
          action={
            trendDelta !== null ? (
              <span className={`kpi-delta ${trendDelta < 0 ? "down" : "up"}`}>
                {trendDelta < 0 ? <TrendingDown size={13} /> : <Zap size={13} />}
                <span className="br-tabular-nums">{trendDelta.toFixed(1)} pts over window</span>
              </span>
            ) : undefined
          }
        >
          {trend.length > 0 ? (
            <TrendChart data={trend} height={220} />
          ) : (
            <EmptyState title="No trend data" />
          )}
        </ChartCard>
      ),
    },
    {
      key: "movers",
      node: (
        <ClickableCard to="/districts" label="Open Districts — match coverage and DQ backlog">
          <ChartCard
            title="District coverage"
            subtitle="Recipient → district match — open the coverage + DQ backlog detail"
            source="account_map"
            loading={isLoading}
          >
            {kpis ? (
              <div className="metric-strip">
                <div className="metric-chip">
                  <span className="mc-label">
                    <Building2Like /> Recipients matched
                  </span>
                  <span className="mc-value br-tabular-nums">
                    {formatInt(kpis.districtsMatched)}
                  </span>
                </div>
                <div className="metric-chip">
                  <span className="mc-label">Unmatched (DQ)</span>
                  <span className="mc-value warn br-tabular-nums">
                    {formatInt(kpis.districtsUnmatched)}
                  </span>
                </div>
                <div className="metric-chip">
                  <span className="mc-label">Recipients total</span>
                  <span className="mc-value br-tabular-nums">{formatInt(kpis.recipients)}</span>
                </div>
              </div>
            ) : (
              <EmptyState title="No district data" />
            )}
          </ChartCard>
        </ClickableCard>
      ),
    },
    {
      key: "thermal",
      node: (
        <ChartCard
          title="Fleet count by source"
          subtitle="The three device tables disagree by ~5k — labeled by source"
          source="overview.fleetCountBySource"
          loading={isLoading}
        >
          {data && data.data.fleetCountBySource.length > 0 ? (
            <DataTable
              rows={data.data.fleetCountBySource}
              rowKey={(r) => r.source}
              columns={[
                { key: "source", header: "Source", render: (r) => <span>{r.source}</span> },
                {
                  key: "count",
                  header: "Distinct",
                  align: "right",
                  render: (r) => <span className="mono">{formatInt(r.count)}</span>,
                },
              ]}
            />
          ) : (
            <EmptyState title="No source breakdown" />
          )}
        </ChartCard>
      ),
    },
    {
      key: "exposure",
      node: (
        <ChartCard
          title="$ exposure"
          subtitle="BusRight's own AR / credit posture — NOT customer revenue"
          source="stg_e2_credits"
          loading={isLoading}
        >
          {kpis ? (
            <div className="metric-strip">
              <div className="metric-chip">
                <span className="mc-label">
                  <CreditCard size={13} /> AR current balance
                </span>
                <span className="mc-value br-tabular-nums">
                  {formatUsdCompact(kpis.arCurrentBalanceUsd)}
                </span>
              </div>
              <div className="metric-chip">
                <span className="mc-label">Recipients (ship-to)</span>
                <span className="mc-value br-tabular-nums">{formatInt(kpis.recipients)}</span>
              </div>
            </div>
          ) : (
            <EmptyState title="No finance data" />
          )}
        </ChartCard>
      ),
    },
  ];

  if (isError) {
    return (
      <>
        <PageHeader title="Executive Command Center" meta={meta} />
        <ChartCard title="Overview">
          <EmptyState
            title="Couldn't load overview"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Executive Command Center"
        description="Fleet posture at a glance — scored health, risk composition, and financial exposure across the hardware fleet."
        meta={meta}
      />
      {insightBanner}
      {kpiBand}
      <div className="grid cols-3">{cards.map((c) => renderCard(c))}</div>
      {meta ? (
        <div style={{ marginTop: 20 }}>
          <ChartCard title="Data notes" loading={isLoading}>
            {isLoading ? <SkeletonLines count={2} /> : <NotesList notes={meta.notes} />}
          </ChartCard>
        </div>
      ) : null}
    </>
  );
};

const renderCard = (c: CardSpec): ReactNode =>
  c.className ? (
    <div key={c.key} className={c.className}>
      {c.node}
    </div>
  ) : (
    <div key={c.key}>{c.node}</div>
  );

// Tiny inline icon alias to keep the import list terse where a building glyph reads better.
const Building2Like = (): ReactNode => <Server size={13} aria-hidden />;
